import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { OrganizationScopeProvider } from "./contexts/OrganizationScopeContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Login } from "./pages/Login";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import { Analytics } from "./pages/Analytics";
import { Organizations } from "./pages/Organizations";
import { Pilots } from "./pages/Pilots";
import { Aircraft } from "./pages/Aircraft";
import AircraftCalendarPage from "./pages/AircraftCalendarPage";
import AircraftDetailPage from "./pages/AircraftDetailPage";
import { Batteries } from "./pages/Batteries";
import { Scenarios } from "./pages/Scenarios";
import { Missions } from "./pages/Missions";
import { MissionDetail } from "./pages/MissionDetail";
import { MissionHistory } from "./pages/MissionHistory";
import { MissionCalendar } from "./pages/MissionCalendar";
import { DailyOpsDashboard } from "./pages/DailyOpsDashboard";
import { Incidents } from "./pages/Incidents";
import { Documents } from "./pages/Documents";
import { UsersPage } from "./pages/Users";
import { Reports } from "./pages/Reports";
import { AlertHistory } from "./pages/AlertHistory";
import { AuditSimulation } from "./pages/AuditSimulation";
import { KnowledgeBaseAdmin } from "./pages/KnowledgeBaseAdmin";
import BulletinManagement from "./pages/BulletinManagement";
import NewsRegulations from "./pages/NewsRegulations";
import InsuranceManagement from "./pages/InsuranceManagement";
import AuthorizationManagement from "./pages/AuthorizationManagement";
import AuthorizationForm from "./pages/AuthorizationForm";
import SubmissionsPage from "./pages/SubmissionsPage";
import Subscription from "./pages/Subscription";
import AdminUsagePage from "./pages/admin/UsagePage";
import OrgFeaturesPage from "./pages/admin/OrgFeaturesPage";
import DroneCatalogPage from "./pages/admin/DroneCatalog";
import CorrespondenceReviewQueuePage from "./pages/admin/CorrespondenceReviewQueue";
import CorrespondencePage from "./pages/CorrespondencePage";
import FleetMap from "./pages/FleetMap";
import { OrganizationScopeGate } from "./components/OrganizationScopeGate";
import "./App.css";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-main)' }}>
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: 'var(--brand-accent)', borderTopColor: 'transparent' }}></div>
          <p style={{ color: 'var(--text-secondary)' }}>Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <OrganizationScopeProvider>
          <BrowserRouter basename={process.env.PUBLIC_URL}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Routes>
                        <Route path="/" element={<Analytics />} />
                        <Route path="/dashboard" element={<Analytics />} />
                        <Route path="/organizations" element={<Organizations />} />
                        <Route path="/pilots" element={<OrganizationScopeGate><Pilots /></OrganizationScopeGate>} />
                        <Route path="/aircraft" element={<OrganizationScopeGate><Aircraft /></OrganizationScopeGate>} />
                        <Route path="/aircraft/calendar" element={<OrganizationScopeGate><AircraftCalendarPage /></OrganizationScopeGate>} />
                        <Route path="/aircraft/:id" element={<OrganizationScopeGate><AircraftDetailPage /></OrganizationScopeGate>} />
                        <Route path="/batteries" element={<OrganizationScopeGate><Batteries /></OrganizationScopeGate>} />
                        <Route path="/scenarios" element={<OrganizationScopeGate><Scenarios /></OrganizationScopeGate>} />
                        <Route path="/missions" element={<OrganizationScopeGate><Missions /></OrganizationScopeGate>} />
                        <Route path="/missions/calendar" element={<OrganizationScopeGate><MissionCalendar /></OrganizationScopeGate>} />
                        <Route path="/missions/history" element={<OrganizationScopeGate><MissionHistory /></OrganizationScopeGate>} />
                        <Route path="/missions/:missionId" element={<OrganizationScopeGate><MissionDetail /></OrganizationScopeGate>} />
                        <Route path="/daily-ops" element={<OrganizationScopeGate><DailyOpsDashboard /></OrganizationScopeGate>} />
                        <Route path="/incidents" element={<OrganizationScopeGate><Incidents /></OrganizationScopeGate>} />
                        <Route path="/documents" element={<OrganizationScopeGate><Documents /></OrganizationScopeGate>} />
                        <Route path="/reports" element={<Reports />} />
                        <Route path="/alerts/history" element={<AlertHistory />} />
                        <Route path="/audits" element={<AuditSimulation />} />
                        <Route path="/knowledge-base" element={<KnowledgeBaseAdmin />} />
                        <Route path="/bulletins" element={<BulletinManagement />} />
                        <Route path="/insurance" element={<OrganizationScopeGate><InsuranceManagement /></OrganizationScopeGate>} />
                        <Route path="/authorizations" element={<OrganizationScopeGate><AuthorizationManagement /></OrganizationScopeGate>} />
                        <Route path="/authorizations/new" element={<OrganizationScopeGate><AuthorizationForm /></OrganizationScopeGate>} />
                        <Route path="/authorizations/:id/edit" element={<OrganizationScopeGate><AuthorizationForm /></OrganizationScopeGate>} />
                        <Route path="/submissions" element={<OrganizationScopeGate><SubmissionsPage /></OrganizationScopeGate>} />
                        <Route path="/subscription" element={<Subscription />} />
                        <Route path="/news" element={<NewsRegulations />} />
                        <Route path="/users" element={<UsersPage />} />
                        <Route path="/admin/usage" element={<AdminUsagePage />} />
                        <Route path="/admin/orgs/:orgId/features" element={<OrgFeaturesPage />} />
                        <Route path="/admin/drone-catalog" element={<DroneCatalogPage />} />
                        <Route path="/admin/correspondence-review" element={<OrganizationScopeGate><CorrespondenceReviewQueuePage /></OrganizationScopeGate>} />
                        <Route path="/correspondence" element={<OrganizationScopeGate><CorrespondencePage /></OrganizationScopeGate>} />
                        <Route path="/fleet-map" element={<OrganizationScopeGate><FleetMap /></OrganizationScopeGate>} />
                      </Routes>
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
            </Routes>
            <Toaster position="top-right" />
          </BrowserRouter>
          </OrganizationScopeProvider>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
